import type { RefObject } from 'react';
import { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ModalInstance } from '../../components/GraphModal/Modal';
import type { GraphEditorInstance } from '../../editor/editor-model';
import type { Author, Message } from '../../types/api';
import type { CreateOrUpdateMessageVariablesSmall } from '../api/message';
import type { EditorCloseCallback } from '../Write/ThreadEditorForm';
import { ThreadEditorForm } from '../Write/ThreadEditorForm';
import { FooterAuthorWithAvatar } from './FooterAuthorWithAvatar';
import { threadFooterMessages } from './messages';

interface FooterModalProps {
  modal: ModalInstance;
  author: Author | undefined;
  serializeId: string;
  mobileAnswerEditorRef: RefObject<GraphEditorInstance>;
  onSubmitAnswer: (variables: CreateOrUpdateMessageVariablesSmall) => Promise<void>;
  currentScrollSectionForced: boolean | Message;
  stopAnsweringAndClear: EditorCloseCallback;
}

export const FooterModalContent = memo(function FooterModal(props: FooterModalProps) {
  const {
    modal,
    author,
    serializeId,
    mobileAnswerEditorRef,
    onSubmitAnswer,
    currentScrollSectionForced,
    stopAnsweringAndClear,
  } = props;
  const { hide /* , show, state */ } = modal;
  const { formatMessage } = useIntl();

  const handleSubmitAnswer = useCallback(
    async (elt: CreateOrUpdateMessageVariablesSmall) => {
      await onSubmitAnswer(elt);
      hide();
    },
    [hide, onSubmitAnswer],
  );

  const footerEditorContentHeader = author ? (
    <MessageAnswerContentHeader author={author} />
  ) : (
    // TODO fallback not required? TBC for the rare cases when the author is not available
    <FormattedMessage {...threadFooterMessages.answer} />
  );

  return (
    <div className="FooterModalContent flex flex-col flex-1 min-h-0 gap-y-3">
      {footerEditorContentHeader}
      <div className="footerEditorContent flex flex-col flex-1 min-h-0 max-h-[60%]">
        <ThreadEditorForm
          key={serializeId}
          editorRef={mobileAnswerEditorRef}
          baseId={serializeId}
          placeholder={formatMessage(threadFooterMessages.newMessage)}
          onSubmitAnswer={handleSubmitAnswer}
          onCancelAnswer={stopAnsweringAndClear}
          hideCancelButton
          // If editing, the edited message is passed.
          message={
            currentScrollSectionForced && currentScrollSectionForced !== true
              ? currentScrollSectionForced
              : undefined
          }
        />
      </div>
    </div>
  );
});

const MessageAnswerContentHeader = memo(function MessageAnswerContentHeader(props: {
  author: Author;
}) {
  const { author } = props;
  return (
    <div className="messageAnswerContentHeader ml-1 flex flex-col gap-y-1">
      <div className="answerTo text-[18px]">
        <FormattedMessage {...threadFooterMessages.answerTo} />
      </div>
      <FooterAuthorWithAvatar author={author} />
    </div>
  );
});
